import { type Snapshot, useRecoilCallback } from 'recoil';
import { PageType } from 'venn-utils';
import {
  type BlockId,
  type BlockOriginalLayout,
  blockContinuationPageCount,
  blockExpandedRows,
  blockExpandedState,
  blockOriginalLayoutState,
  continuationUserBlocksState,
  isContinuationLayoutId,
  makeContinuationLayoutId,
  planNeighborsAfterExpansion,
  RL_GRID_LANDSCAPE_NO_COLUMNS,
  RL_GRID_PORTRAIT_NO_COLUMNS,
  studioMaxUsableRows,
  studioPageUsableRows,
  studioPrintOrientationType,
  viewPages,
} from 'venn-state';
import type { Layout } from 'react-grid-layout';
import type { Page } from '../contexts/studio-context';

type UseExpandBlockResult = {
  expand: () => Promise<void>;
  contract: () => Promise<void>;
};

type UseExpandBlockActionsResult = {
  expand: (blockId: BlockId) => Promise<void>;
  contract: (blockId: BlockId) => Promise<void>;
};

const findSourcePageIndex = (pages: readonly Page[], blockId: BlockId): number =>
  pages.findIndex((page) => page.type === PageType.GRID && page.layout.some((l) => l.i === blockId));

const restoreSourceLayout = (page: Page, blockId: BlockId, original: BlockOriginalLayout): Page => ({
  ...page,
  layout: page.layout.map((l) => (l.i === blockId ? { ...l, x: original.x, w: original.w, h: original.h ?? l.h } : l)),
});

export const normalizeLayoutY = (layout: Layout): Layout => {
  if (layout.length === 0) return [];
  const minY = Math.min(...layout.map((l) => l.y));
  return layout.map((l) => ({ ...l, y: l.y - minY }));
};

type ContinuationHostedBlock = {
  sliceLayoutId: string;
  existingUserBlocks: Layout;
  /** viewPages index of the grid page whose continuation chain hosts the block. */
  rootPageIndex: number;
};

const collectOwnedSlices = async ({
  snapshot,
  ownerId,
  rootPageIndex,
  visited,
}: {
  snapshot: Snapshot;
  ownerId: BlockId;
  rootPageIndex: number;
  visited: Set<BlockId>;
}): Promise<ContinuationHostedBlock[]> => {
  if (visited.has(ownerId)) return [];
  visited.add(ownerId);

  const continuationCount = await snapshot.getPromise(blockContinuationPageCount(ownerId));
  const slices = await Promise.all(
    Array.from({ length: continuationCount }, async (_, continuationIndex) => {
      const sliceLayoutId = makeContinuationLayoutId({ sourceBlockId: ownerId, continuationIndex });
      const existingUserBlocks = await snapshot.getPromise(continuationUserBlocksState(sliceLayoutId));
      return { sliceLayoutId, existingUserBlocks, rootPageIndex };
    }),
  );
  const nested = await Promise.all(
    slices.flatMap(({ existingUserBlocks }) =>
      existingUserBlocks.map((hosted) => collectOwnedSlices({ snapshot, ownerId: hosted.i, rootPageIndex, visited })),
    ),
  );
  return [...slices, ...nested.flat()];
};

// Blocks dropped onto a continuation page live only in continuationUserBlocksState, never in pages[].layout.
const findContinuationHostedBlock = async (
  snapshot: Snapshot,
  pages: readonly Page[],
  blockId: BlockId,
): Promise<ContinuationHostedBlock | undefined> => {
  const visited = new Set<BlockId>();
  const slicesPerOwner = await Promise.all(
    pages.flatMap((page, rootPageIndex) =>
      page.type === PageType.GRID
        ? page.layout.map((layout) => collectOwnedSlices({ snapshot, ownerId: layout.i, rootPageIndex, visited }))
        : [],
    ),
  );
  return slicesPerOwner.flat().find((slice) => slice.existingUserBlocks.some((l) => l.i === blockId));
};

// The count must be read before clearing expansion state, since blockContinuationPageCount returns 0 once
// blockExpandedState is false.
const readOwnContinuations = async (
  snapshot: Snapshot,
  blockId: BlockId,
): Promise<{ continuationKeys: string[]; userBlocks: Layout }> => {
  const continuationCount = await snapshot.getPromise(blockContinuationPageCount(blockId));
  const continuationKeys = Array.from({ length: continuationCount }, (_, c) =>
    makeContinuationLayoutId({ sourceBlockId: blockId, continuationIndex: c }),
  );
  const userBlocksPerSlice = await Promise.all(
    continuationKeys.map((key) => snapshot.getPromise(continuationUserBlocksState(key))),
  );
  return { continuationKeys, userBlocks: userBlocksPerSlice.flat() };
};

const insertPageAfter = (pages: readonly Page[], index: number, page: Page): Page[] => [
  ...pages.slice(0, index + 1),
  page,
  ...pages.slice(index + 1),
];

/**
 * Returns the next state for a continuation page during contract:
 *   - `null`              → drop (page contained only the continuation placeholder)
 *   - `Page` (CONTINUATION) → leave as-is (continuation belongs to a different block)
 *   - `Page` (GRID)       → convert (page contained user-dropped blocks; promote to a regular grid page)
 */
export const reconcileContinuationOnContract = (page: Page, blockId: BlockId): Page | null => {
  if (page.type !== PageType.CONTINUATION) return page;
  if (page.continuation.sourceBlockId !== blockId) return page;

  const userBlocks = page.layout.filter((l) => !isContinuationLayoutId(l.i));
  if (userBlocks.length === 0) return null;

  return {
    type: PageType.GRID,
    layout: normalizeLayoutY(userBlocks),
  };
};

export const useExpandBlockActions = (): UseExpandBlockActionsResult => {
  const expand = useRecoilCallback(
    ({ set, snapshot }) =>
      async (blockId: BlockId) => {
        const pages = await snapshot.getPromise(viewPages);
        const orientation = await snapshot.getPromise(studioPrintOrientationType);
        const noColumns = orientation === 'PORTRAIT' ? RL_GRID_PORTRAIT_NO_COLUMNS : RL_GRID_LANDSCAPE_NO_COLUMNS;

        const sourceIndex = findSourcePageIndex(pages, blockId);
        if (sourceIndex < 0) {
          const hosted = await findContinuationHostedBlock(snapshot, pages, blockId);
          if (!hosted) return;

          const { sliceLayoutId, existingUserBlocks, rootPageIndex } = hosted;
          const sourceLayout = existingUserBlocks.find((l) => l.i === blockId);
          if (!sourceLayout) return;

          const existingSnapshot = await snapshot.getPromise(blockOriginalLayoutState(blockId));
          if (!existingSnapshot) {
            set(blockOriginalLayoutState(blockId), { x: sourceLayout.x, w: sourceLayout.w, h: sourceLayout.h });
          }

          const [expandedRows, pageUsableRows] = await Promise.all([
            snapshot.getPromise(blockExpandedRows(blockId)),
            snapshot.getPromise(studioPageUsableRows),
          ]);

          const { toKeep, stayBelow, spillover } = planNeighborsAfterExpansion({
            layout: existingUserBlocks,
            expandingBlockId: blockId,
            expandedRows,
            maxRows: pageUsableRows,
          });

          set(continuationUserBlocksState(sliceLayoutId), [
            ...toKeep.map((l) => (l.i === blockId ? { ...l, x: 0, w: noColumns, h: expandedRows } : l)),
            ...stayBelow,
          ]);

          if (spillover.length > 0) {
            // Continuation pages aren't entries in viewPages, so insert after the grid page that heads the chain (as GridPage's onDrop does).
            const spilloverPage: Page = { type: PageType.GRID, layout: normalizeLayoutY(spillover) };
            set(viewPages, (current) => insertPageAfter(current, rootPageIndex, spilloverPage));
          }

          set(blockExpandedState(blockId), true);
          return;
        }

        const sourcePage = pages[sourceIndex]!;
        const sourceLayout = sourcePage.layout.find((l) => l.i === blockId);
        if (!sourceLayout) return;

        const existingSnapshot = await snapshot.getPromise(blockOriginalLayoutState(blockId));
        if (!existingSnapshot) {
          set(blockOriginalLayoutState(blockId), { x: sourceLayout.x, w: sourceLayout.w, h: undefined });
        }

        const expandedRows = await snapshot.getPromise(blockExpandedRows(blockId));
        const maxRows = await snapshot.getPromise(studioMaxUsableRows);

        const { toKeep, stayBelow, spillover } = planNeighborsAfterExpansion({
          layout: sourcePage.layout,
          expandingBlockId: blockId,
          expandedRows,
          maxRows,
        });

        set(viewPages, (current) => {
          const next = current.map((page, idx) => {
            if (idx !== sourceIndex) return page;
            return {
              ...page,
              layout: [...toKeep.map((l) => (l.i === blockId ? { ...l, x: 0, w: noColumns } : l)), ...stayBelow],
            };
          });

          if (spillover.length === 0) return next;

          return insertPageAfter(next, sourceIndex, { type: PageType.GRID, layout: normalizeLayoutY(spillover) });
        });

        set(blockExpandedState(blockId), true);
      },
    [],
  );

  const contract = useRecoilCallback(
    ({ set, snapshot }) =>
      async (blockId: BlockId) => {
        const pages = await snapshot.getPromise(viewPages);
        const [original, { continuationKeys, userBlocks: allContinuationUserBlocks }] = await Promise.all([
          snapshot.getPromise(blockOriginalLayoutState(blockId)),
          readOwnContinuations(snapshot, blockId),
        ]);

        if (findSourcePageIndex(pages, blockId) < 0) {
          const hosted = await findContinuationHostedBlock(snapshot, pages, blockId);
          if (hosted) {
            set(
              continuationUserBlocksState(hosted.sliceLayoutId),
              hosted.existingUserBlocks.map((l) =>
                l.i === blockId && original ? { ...l, x: original.x, w: original.w, h: original.h ?? l.h } : l,
              ),
            );
            if (allContinuationUserBlocks.length > 0) {
              const userPage: Page = { type: PageType.GRID, layout: normalizeLayoutY(allContinuationUserBlocks) };
              set(viewPages, (current) => insertPageAfter(current, hosted.rootPageIndex, userPage));
            }
          }
          for (const key of continuationKeys) {
            set(continuationUserBlocksState(key), []);
          }
          set(blockOriginalLayoutState(blockId), undefined);
          set(blockExpandedState(blockId), false);
          return;
        }

        set(viewPages, (current) => {
          const sourceIndex = findSourcePageIndex(current, blockId);

          const next: Page[] = [];
          let nextSourceIdx = -1;
          for (let i = 0; i < current.length; i++) {
            const page = current[i]!;
            const reconciled = i === sourceIndex && original ? restoreSourceLayout(page, blockId, original) : page;
            const continuationResult = reconcileContinuationOnContract(reconciled, blockId);
            if (continuationResult !== null) {
              if (i === sourceIndex) nextSourceIdx = next.length;
              next.push(continuationResult);
            }
          }

          // Promote any user-dropped blocks onto continuation-page to a new GRID page.
          if (allContinuationUserBlocks.length > 0 && nextSourceIdx >= 0) {
            return insertPageAfter(next, nextSourceIdx, {
              type: PageType.GRID,
              layout: normalizeLayoutY(allContinuationUserBlocks),
            });
          }
          return next;
        });

        // Clear the keyed store entries for this block's continuations.
        for (const key of continuationKeys) {
          set(continuationUserBlocksState(key), []);
        }

        set(blockOriginalLayoutState(blockId), undefined);
        set(blockExpandedState(blockId), false);
      },
    [],
  );

  return { expand, contract };
};

export const useExpandBlock = (blockId: BlockId): UseExpandBlockResult => {
  const { expand, contract } = useExpandBlockActions();
  return {
    expand: () => expand(blockId),
    contract: () => contract(blockId),
  };
};
